/**
 * Checkout invoice form schema
 * Custom validation for billing/invoice information
 */

import { z } from 'zod';
import { createBaseFieldSchemas } from '../base-fields';
import { ITranslationContext } from '../../utils/resources/types';
import { VAT_NUMBER_REGEX, FISCAL_CODE_REGEX, SDI_CODE_REGEX } from '../../utils/regexes/common';

/**
 * Get checkout invoice schema with optional translation context
 * @param translationContext - Optional i18n translation context
 * @param countryCode - Selected billing country code
 * @param invoiceType - 1 for person, 2 for organization
 */
export const getCheckoutInvoiceSchema = (translationContext?: ITranslationContext, countryCode?: string, invoiceType?: number) => {
    const schemas = createBaseFieldSchemas(translationContext);
    const isItalian = countryCode === 'ITA';

    // Base schema for all invoice types
    const baseSchema = z.object({
        billing: z.boolean(),
        Type_CAP: z
            .union([z.number(), z.string()])
            .transform(val => (typeof val === 'string' ? parseInt(val, 10) : val))
            .pipe(
                z
                    .number()
                    .min(1, 'Invoice type is required')
                    .max(2)
            ),
        Country_CAP: z.string().min(3, 'Country is required'),
        Street_CAP: z.string().min(1, 'Street is required'),
        StreetNumber_CAP: z.string().min(1, 'Street number is required'),
        City_CAP: z.string().min(1, 'City is required'),
        ZipCode_CAP: countryCode ? schemas.postalCodeWithCountrySchema(countryCode) : z.string().min(3, 'Postal code is required')
    });

    // Person-specific fields (Type_CAP === 1)
    const personFields = {
        FirstName_CAP: schemas.nameSchema,
        LastName_CAP: schemas.nameSchema,
        FiscalCode_CAP: isItalian
            ? z
                  .string()
                  .min(11, 'Fiscal code must be 11 or 16 characters')
                  .max(16, 'Fiscal code must be 11 or 16 characters')
                  .regex(FISCAL_CODE_REGEX, 'Invalid fiscal code format')
            : z.string().optional()
    };

    // Organization-specific fields (Type_CAP === 2)
    const organizationFields = {
        OrgName_CAP: z.string().min(2, 'Organization name is required'),
        VatNum_CAP: z
            .string()
            .max(15, 'VAT number cannot exceed 15 characters')
            .regex(VAT_NUMBER_REGEX, 'VAT number must contain only letters and numbers')
            .optional()
            .or(z.literal('')),
        FiscalCode_CAP: isItalian
            ? z
                  .string()
                  .min(11, 'Fiscal code must be 11 or 16 characters')
                  .max(16, 'Fiscal code must be 11 or 16 characters')
                  .regex(FISCAL_CODE_REGEX, 'Invalid fiscal code format')
            : z.string().optional(),
        PEC_CAP: isItalian ? schemas.emailSchema : z.string().optional(),
        SDI_CAP: isItalian
            ? z
                  .string()
                  .length(7, 'SDI code must be 7 characters')
                  .regex(SDI_CODE_REGEX, 'Invalid SDI code format')
            : z.string().optional()
    };

    // Optional fields
    const optionalFields = {
        InvoiceCustAccount_CAP: z.string().optional()
    };

    // Build the complete schema based on invoice type
    if (invoiceType === 1) {
        // Person schema
        return baseSchema.extend({
            ...personFields,
            ...optionalFields
        });
    } else if (invoiceType === 2) {
        // Organization schema
        return baseSchema.extend({
            ...organizationFields,
            ...optionalFields
        });
    } else {
        // Generic schema (when type not yet selected or for validation of all fields)
        return baseSchema
            .extend({
                ...personFields,
                ...organizationFields,
                ...optionalFields
            })
            .partial({
                FirstName_CAP: true,
                LastName_CAP: true,
                OrgName_CAP: true,
                VatNum_CAP: true,
                FiscalCode_CAP: true,
                PEC_CAP: true,
                SDI_CAP: true,
                InvoiceCustAccount_CAP: true
            });
    }
};

// Type inference
export type CheckoutInvoiceFormData = z.infer<ReturnType<typeof getCheckoutInvoiceSchema>>;

// Re-export validation patterns for backward compatibility
export { FISCAL_CODE_REGEX as fiscalCodePattern, SDI_CODE_REGEX as sdiPattern, VAT_NUMBER_REGEX as vatPattern };
